import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { LINE_STATUS } from '../../types/pettyCash';
import type { AccountingDocument, AccountingEntry, AccountingPreview, DimensionInfo, LineCheck } from '../../types/pettyCash';
import { formatAmount, formatDate } from '../../utils/pettyCash';

/**
 * Finance review: every SAP document the claim will create (per receipt one service A/P invoice, paid by a
 * cash payment, or for a holder with an auxiliary by a journal entry reconciled with it), the net effect by
 * account, and the checks.
 */

interface Props {
  preview: AccountingPreview | null;
  loading: boolean;
  error: string | null;
  currency: string;
  dimensions: DimensionInfo[];
  /** Finance may change a line from here. */
  onChangeLine?: (lineId: number) => void;
  /** Which lines may change now (default: all); after a posting failure only the receipts not yet in SAP. */
  canChangeLine?: (lineId: number) => boolean;
  /** Label of the change button ("Change" in the finance review, "Correct" after a posting failure). */
  changeLabel?: string;
}

const dimsText = (e: AccountingEntry, dimensions: DimensionInfo[]): string => {
  const parts: string[] = [];
  if (e.projectCode) parts.push(e.projectCode);
  for (const d of dimensions) {
    const v = e.dims?.[String(d.code)];
    if (v) parts.push(v);
  }
  return parts.join(' · ');
};

export function CheckList({ checks }: { checks: LineCheck[] }) {
  if (!checks.length) return <Typography variant="body2" color="text.secondary">No problems found.</Typography>;
  return (
    <Stack spacing={1}>
      {checks.map((c, i) => (
        <Stack key={`${c.code}-${i}`} direction="row" spacing={1} alignItems="flex-start">
          {c.level === 'ok' ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#1e6b2a' }} aria-hidden="true" />
            : c.level === 'warning' ? <WarningAmberIcon fontSize="small" sx={{ color: '#9a5b00' }} aria-hidden="true" />
              : <ErrorOutlineIcon fontSize="small" color="error" aria-hidden="true" />}
          <Typography variant="body2">{c.message}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function DocumentBlock({ doc, currency, dimensions, onChangeLine, changeLabel }: {
  doc: AccountingDocument; currency: string; dimensions: DimensionInfo[]; onChangeLine?: (lineId: number) => void; changeLabel: string;
}) {
  const status = LINE_STATUS[doc.status];
  const muted = doc.disallowed;
  const row = (e: AccountingEntry, key: string, first: boolean, rows: number, title: React.ReactNode) => (
    <TableRow key={key} sx={muted ? { opacity: 0.55 } : undefined}>
      {first ? <TableCell rowSpan={rows} sx={{ verticalAlign: 'top', minWidth: 180 }}>{title}</TableCell> : null}
      <TableCell>{e.account} {e.accountName || ''}{e.bp ? <Typography component="span" variant="caption" color="text.secondary"> · {e.bp}</Typography> : null}</TableCell>
      <TableCell><Typography variant="caption" color="text.secondary">{dimsText(e, dimensions)}</Typography></TableCell>
      <TableCell align="right">{e.debit ? formatAmount(e.debit) : ''}</TableCell>
      <TableCell align="right">{e.credit ? formatAmount(e.credit) : ''}</TableCell>
    </TableRow>
  );
  const inv = doc.invoice;
  const pay = doc.payment;
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 1.5, py: 1, bgcolor: '#fafbfb', borderBottom: '1px solid #e3e8eb' }}>
        <Typography variant="body2" fontWeight={600}>Line {doc.lineNum}</Typography>
        <Chip size="small" label={doc.lineType === 'VAT' ? 'VAT' : 'Non-VAT'} color={doc.lineType === 'VAT' ? 'info' : 'default'} variant={doc.lineType === 'VAT' ? 'filled' : 'outlined'} />
        <Typography variant="body2">{inv.cardCode} {inv.cardName} · {inv.numAtCard} · {formatDate(inv.taxDate)}</Typography>
        {doc.disallowed ? <Chip size="small" color="warning" label="Disallowed: not posted" /> : doc.status !== 'Open' ? <Chip size="small" color={status.color} label={status.label} /> : null}
        {doc.sapInvoiceNum ? <Typography variant="caption">A/P invoice {doc.sapInvoiceNum}</Typography> : null}
        {doc.sapPaymentNum ? <Typography variant="caption">{pay.kind === 'Journal' ? 'Journal entry' : 'Payment'} {doc.sapPaymentNum}</Typography> : null}
        <Box sx={{ flexGrow: 1 }} />
        {onChangeLine ? <Button size="small" variant="outlined" onClick={() => onChangeLine(doc.lineId)}>{changeLabel}</Button> : null}
      </Stack>
      <TableContainer>
        <Table size="small" aria-label={`SAP entries of line ${doc.lineNum}`} sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Dimensions</TableCell>
              <TableCell align="right">Debit {currency}</TableCell>
              <TableCell align="right">Credit {currency}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inv.entries.map((e, i) => row(e, `i${i}`, i === 0, inv.entries.length, (
              <>
                <Typography variant="body2" fontWeight={600}>A/P invoice, service</Typography>
                <Typography variant="caption" color="text.secondary">Vendor ref. {inv.numAtCard} · document date {formatDate(inv.taxDate)} · posting {formatDate(inv.docDate)}{inv.vatCode ? ` · ${inv.vatCode}` : ''}</Typography>
              </>
            )))}
            {pay.entries.map((e, i) => row(e, `p${i}`, i === 0, pay.entries.length, pay.kind === 'Journal' ? (
              <>
                <Typography variant="body2" fontWeight={600}>Journal entry, paid in cash</Typography>
                <Typography variant="caption" color="text.secondary">
                  From {pay.cashAccount} {pay.cashAccountName || ''} with auxiliary {pay.auxiliary}, reconciled with the invoice
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" fontWeight={600}>Outgoing payment, cash</Typography>
                <Typography variant="caption" color="text.secondary">From {pay.cashAccount} {pay.cashAccountName || ''}, applied in full to the invoice</Typography>
              </>
            )))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

const AccountingView: React.FC<Props> = ({ preview, loading, error, currency, dimensions, onChangeLine, canChangeLine, changeLabel = 'Change' }) => {
  if (loading && !preview) return <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Building the SAP entries…</Typography></Stack>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!preview) return null;
  const posted = preview.documents.filter((d) => !d.disallowed);
  const byJournal = posted.some((d) => d.payment.kind === 'Journal');
  const auxiliary = posted.find((d) => d.payment.auxiliary)?.payment.auxiliary;
  const s = posted.length === 1 ? '' : 's';
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {byJournal
          ? `${posted.length} service A/P invoice${s}, each paid by a journal entry (Dr the supplier, Cr ${preview.cashAccount} ${preview.cashAccountName || ''} with the holder's auxiliary ${auxiliary || ''}) reconciled with it`
          : `${posted.length * 2} SAP documents: ${posted.length} service A/P invoice${s} and ${posted.length} outgoing payment${s} in cash from ${preview.cashAccount} ${preview.cashAccountName || ''}`}
        , posting date {formatDate(preview.postingDate)}.
        {preview.documents.length > posted.length ? ` ${preview.documents.length - posted.length} disallowed receipt${preview.documents.length - posted.length === 1 ? ' is' : 's are'} left out.` : ''}
      </Typography>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
        <Stack spacing={2} sx={{ flex: '2 1 0', minWidth: 0, width: '100%' }}>
          {preview.documents.map((d) => (
            <DocumentBlock
              key={d.lineId}
              doc={d}
              currency={currency}
              dimensions={dimensions}
              onChangeLine={onChangeLine && (!canChangeLine || canChangeLine(d.lineId)) ? onChangeLine : undefined}
              changeLabel={changeLabel}
            />
          ))}
        </Stack>
        <Stack spacing={2} sx={{ flex: '1 1 0', minWidth: 280, width: '100%' }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Net effect by account</Typography>
            <TableContainer>
              <Table size="small" aria-label="Net effect by account">
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell align="right">Debit</TableCell>
                    <TableCell align="right">Credit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.net.map((e) => (
                    <TableRow key={e.account}>
                      <TableCell>{e.account} {e.accountName || ''}</TableCell>
                      <TableCell align="right">{e.debit ? formatAmount(e.debit) : ''}</TableCell>
                      <TableCell align="right">{e.credit ? formatAmount(e.credit) : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Total {currency}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>{formatAmount(preview.totals.debit)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>{formatAmount(preview.totals.credit)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary">Vendor accounts net to zero on every line.</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Checks</Typography>
            <CheckList checks={preview.checks} />
          </Paper>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AccountingView;
